// CERBERUS, "The Junkyard Warden": the boss island's fight (Godot scripts/enemies/cerberus.gd,
// docs/BOSS_GRAMMAR.md §3), ported to the browser sim. Server-only brain, like enemies.ts.
//
// A twenty-metre crane walker with three shipping-container heads. One attack at a time
// (TELL → EXECUTE → RECOVER), each owned by a head:
//   FURNACE (+X, fire)  SCORCH SWEEP  a flame arc along the ground: jump or fly over it
//   DYNAMO  (centre)    VOLT VOLLEY   three fans of slow bolts: the bullet ballet
//   MAGNET  (-X)        CRUSH LUNGE   a telegraphed charge down a lane: roll out of it
//   body                TRIPLE BARK   a shockwave ring: jump it
//   body                JUNK FLING    boulders lobbed at everyone, landing zones marked
//   body (phase 3)      SONIC HOWL    2.5 s wind-up, then it hits everyone it can SEE:
//                                    break line of sight behind a pillar, rock or building
// Weak points (kit parity: every class has a way in):
//   the head that just attacked hangs its jaw open for 4 s: mouth AV0 ×1.25 (gold)
//   three collar tags on the cab, always lit: AV1 ×0.6 (gold, the any-kit path)
//   container heads AV2 (a break pool each: empty one and that head dies for good, its
//     attack leaves the roster, the stump stays an AV0 wound, the others speed up)
//   after the Howl the furnace core opens for 4 s: AV0 ×1.5
//   everything else is AV3 plating (it clangs)
// At 66 % it raises STATIC RUFF, the hyper shield: prime it, then detonate (or grind the
// crack meter, so no posse is ever locked out). Below 33 % the Howl joins the roster.

import { DT, Rng, clamp, len2, raySphere, secs, wrapAngle } from './core';
import { CERBERUS_SCALE, ENEMIES, EType } from './defs';
import { BKind, BULLETS, lobVelocity } from './bullets';
import { EState, Enemy } from './enemies';
import { PlayerState, isTargetable } from './player';
import { NO_GROUND, World } from './world';
import { BossKind } from './bosses/types';

export const enum BAtk { None = 0, Bark = 1, Lunge = 2, Scorch = 3, Volley = 4, Fling = 5, Howl = 6 }
export const BOSS_ATTACK_NAMES = ['', 'TRIPLE BARK', 'CRUSH LUNGE', 'SCORCH SWEEP', 'VOLT VOLLEY', 'JUNK FLING', 'SONIC HOWL'];
/** heads, in model order: furnace +X (fire), dynamo centre (shock), magnet -X */
export const enum Head { Furnace = 0, Dynamo = 1, Magnet = 2 }
export const HEAD_NAMES = ['FURNACE', 'DYNAMO', 'MAGNET'];

/** Which part a shot found. */
export const enum Part { None = 0, Plate = 1, Head = 2, Mouth = 3, Tag = 4, Core = 5 }

// Hit volumes in MODEL space (metres, model faces +Z, Assets/bosses/cerberus/cerberus.glb),
// scaled by CERBERUS_SCALE. The renderer poses the GLB with the same yaw convention.
const S = CERBERUS_SCALE;
const HEADS = [
  { x: 5.6, y: 17.3, z: 1.8, r: 3.0, mx: 5.39, my: 15.5, mz: 3.6 },
  { x: 0.0, y: 18.6, z: 2.0, r: 3.1, mx: 0.0, my: 16.6, mz: 4.2 },
  { x: -5.6, y: 17.3, z: 1.8, r: 3.0, mx: -5.39, my: 15.5, mz: 3.6 },
];
const MOUTH_R = 1.7;
const TAGS = [[-3.26, 8.65, 1.25], [-1.9, 9.15, 2.1], [3.26, 8.65, 1.25]];
const TAG_R = 0.8;
const CORE = [0, 9.0, 2.3], CORE_R = 1.2;
const PLATES = [
  [0, 9.6, 0, 3.7], // cab
  [3.3, 6.2, 0, 1.5], [-3.3, 6.2, 0, 1.5], // legs
  [3.6, 2.2, 0.4, 2.4], [-3.6, 2.2, 0.4, 2.4], // tracked feet
];

const VENT_TICKS = secs(4);
const CORE_TICKS = secs(4);
const STAGGER_TICKS = secs(3);
const CRACK = 1300; // raw damage that shatters the Ruff without a detonation
const KEEP = 13; // preferred distance to its target
const ARENA_R = 40;

export interface BossState {
  kind: BossKind.Cerberus;
  eid: number;
  headHp: number[];
  headMax: number;
  /** ticks a head's mouth hangs open; -1 = broken (open for good) */
  mouth: number[];
  coreT: number;
  /** 0 patrol, 1 after the Ruff, 2 below 33 % (Howl) */
  phase: number;
  ruffDone: boolean;
  crack: number;
  atk: BAtk;
  cd: number[];
  beat: number;
  cadence: number;
  hitMask: number; // players hit by this attack (bit per pid)
  lungeX: number; lungeZ: number;
  sweep: number; // current scorch angle (world yaw)
  introT: number;
  addsT: number;
  /** howl victims this attack, for the HUD (cover or not) */
  howlLos: number;
}

export function makeBossState(e: Enemy, players: number): BossState {
  const headMax = 380 * (0.7 + 0.3 * Math.max(1, players)) * (e.maxHp / (ENEMIES[EType.Cerberus].hp));
  return {
    kind: BossKind.Cerberus, eid: e.id, headHp: [headMax, headMax, headMax], headMax, mouth: [0, 0, 0], coreT: 0,
    phase: 0, ruffDone: false, crack: 0, atk: BAtk.None,
    cd: [0, secs(4), secs(6), secs(7), secs(3), secs(9), secs(6)], beat: secs(2.5), cadence: 1,
    hitMask: 0, lungeX: 0, lungeZ: 0, sweep: 0, introT: secs(2.2), addsT: secs(18), howlLos: 0,
  };
}

/** What the boss needs from the game (a superset of the robot AiHost). */
export interface BossHost {
  world: World;
  tick: number;
  rng: Rng;
  players: Map<number, PlayerState>;
  enemies: Enemy[];
  fireBullet(kind: BKind, ox: number, oy: number, oz: number, vx: number, vy: number, vz: number, owner: number): void;
  hurtPlayer(p: PlayerState, dmg: number, fx: number, fz: number, flags: number, kx?: number, kz?: number, bypassShield?: boolean): boolean;
  bossEvent(kind: BossEv, a: number, b: number): void;
  spawnAdds(n: number): void;
}
export const enum BossEv { Intro = 0, Attack = 1, HeadBreak = 2, Ruff = 3, RuffBreak = 4, Phase3 = 5, Howl = 6, CoreOpen = 7, Dead = 8 }

// ------------------------------------------------------------------ geometry
/** Model space → world, with the renderer's convention (group.rotation.y = yaw + π). */
export function bossToWorld(x: number, y: number, z: number, yaw: number, lx: number, ly: number, lz: number, out: { x: number; y: number; z: number }) {
  const c = Math.cos(yaw + Math.PI), s = Math.sin(yaw + Math.PI);
  lx *= S; ly *= S; lz *= S;
  out.x = x + lx * c + lz * s;
  out.y = y + ly;
  out.z = z - lx * s + lz * c;
  return out;
}

const tmp = { x: 0, y: 0, z: 0 };
export interface PartHit { t: number; part: Part; head: number }

/**
 * Ray against the boss at a (rewound) pose. `mouths` gives which mouths are open
 * (bit per head) and `core` whether the furnace core is open; closed weak points are skipped
 * (the shot hits the plate or head behind them). Returns the nearest part, or t = -1.
 */
export function bossRaycast(
  x: number, y: number, z: number, yaw: number, mouths: number, core: boolean,
  ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxT: number, out: PartHit,
): PartHit {
  out.t = -1; out.part = Part.None; out.head = -1;
  let best = maxT;
  const test = (lx: number, ly: number, lz: number, r: number, part: Part, head: number, bias = 0) => {
    bossToWorld(x, y, z, yaw, lx, ly, lz, tmp);
    const t = raySphere(ox, oy, oz, dx, dy, dz, tmp.x, tmp.y, tmp.z, r * S);
    if (t >= 0 && t - bias < best) { best = t - bias; out.t = t; out.part = part; out.head = head; }
  };
  // weak points first, with a small bias so a gold spot wins a tie with the plate it sits on
  for (let i = 0; i < 3; i++) if (mouths & (1 << i)) test(HEADS[i].mx, HEADS[i].my, HEADS[i].mz, MOUTH_R, Part.Mouth, i, 0.6);
  for (const tg of TAGS) test(tg[0], tg[1], tg[2], TAG_R, Part.Tag, -1, 0.5);
  if (core) test(CORE[0], CORE[1], CORE[2], CORE_R, Part.Core, -1, 0.6);
  for (let i = 0; i < 3; i++) test(HEADS[i].x, HEADS[i].y, HEADS[i].z, HEADS[i].r, Part.Head, i);
  for (const pl of PLATES) test(pl[0], pl[1], pl[2], pl[3], Part.Plate, -1);
  return out;
}

/** Armor value and damage multiplier per part (BOSS_GRAMMAR hittability table). */
export function partArmor(part: Part): { av: number; mul: number } {
  switch (part) {
    case Part.Mouth: return { av: 0, mul: 1.25 };
    case Part.Core: return { av: 0, mul: 1.5 };
    case Part.Tag: return { av: 1, mul: 0.6 };
    case Part.Head: return { av: 2, mul: 1 };
    default: return { av: 3, mul: 1 };
  }
}

export function mouthBits(b: BossState) {
  return (b.mouth[0] !== 0 ? 1 : 0) | (b.mouth[1] !== 0 ? 2 : 0) | (b.mouth[2] !== 0 ? 4 : 0);
}

/** Feed a landed hit into the head pools / crack meter. `dealt` is after armor. */
export function bossTookHit(h: BossHost, e: Enemy, b: BossState, part: Part, head: number, dealt: number) {
  if (e.hyper) {
    b.crack += dealt * 10; // dealt is already cut to 10 %; the meter counts what you threw
    if (b.crack >= CRACK) shatterRuff(h, e, b);
  }
  if ((part === Part.Head || part === Part.Mouth) && head >= 0 && b.mouth[head] !== -1) {
    b.headHp[head] -= part === Part.Mouth ? dealt * 0.5 : dealt;
    if (b.headHp[head] <= 0) breakHead(h, e, b, head);
  }
}

function breakHead(h: BossHost, e: Enemy, b: BossState, head: number) {
  b.headHp[head] = 0;
  b.mouth[head] = -1;
  b.cadence *= 0.87;
  e.stunT = Math.max(e.stunT, STAGGER_TICKS);
  if (b.atk !== BAtk.None && attackHead(b.atk) === head) endAttack(e, b, secs(0.5));
  h.bossEvent(BossEv.HeadBreak, head, 0);
}

export function shatterRuff(h: BossHost, e: Enemy, b: BossState) {
  if (!e.hyper) return;
  e.hyper = 0;
  b.crack = 0;
  e.stunT = Math.max(e.stunT, STAGGER_TICKS);
  h.bossEvent(BossEv.RuffBreak, 0, 0);
}

function attackHead(a: BAtk) {
  return a === BAtk.Scorch ? Head.Furnace : a === BAtk.Volley ? Head.Dynamo : a === BAtk.Lunge ? Head.Magnet : -1;
}

// ------------------------------------------------------------------ the brain
function setSt(e: Enemy, st: EState, dur: number) { e.st = st; e.stT = 0; e.stDur = dur; }

function endAttack(e: Enemy, b: BossState, rec: number) {
  const hd = attackHead(b.atk);
  if (hd >= 0 && b.mouth[hd] !== -1) b.mouth[hd] = VENT_TICKS; // the jaw hangs open: shoot into it
  if (b.atk === BAtk.Howl) b.coreT = CORE_TICKS;
  setSt(e, EState.Recover, rec);
}

const TELL = [0, 0.9, 0.8, 1.0, 0.7, 0.8, 2.5];
const EXEC = [0, 1.0, 0.9, 0.9, 1.2, 0.6, 0.4];
const REC = [0, 1.4, 1.8, 1.8, 1.4, 1.2, 4.0];
const CD: [number, number][] = [[0, 0], [9, 14], [10, 15], [10, 15], [6, 10], [11, 17], [32, 42]];

function groundAt(w: World, x: number, z: number, y: number) {
  const g = w.terrain(x, z);
  return g === NO_GROUND ? y : g;
}

function targets(h: BossHost) {
  const out: PlayerState[] = [];
  for (const p of h.players.values()) if (isTargetable(p) && p.phase === 0) out.push(p);
  return out;
}

export function stepCerberus(e: Enemy, b: BossState, h: BossHost) {
  e.stT++;
  for (let i = 0; i < 3; i++) if (b.mouth[i] > 0) b.mouth[i]--;
  if (b.coreT > 0) b.coreT--;
  for (let i = 1; i < b.cd.length; i++) if (b.cd[i] > 0) b.cd[i]--;
  if (e.vulnT > 0) e.vulnT--;
  if (e.stripT > 0) e.stripT--;
  if (e.burnT > 0) { e.burnT--; e.hp -= 6 * DT; }
  // priming flinches a robot for 0.75 s; a twenty-metre dog only staggers for real
  if (e.stunT > 0 && e.stunT < secs(1) && e.st !== EState.Stun) e.stunT = 0;

  // ---- intro: it lands in the arena
  if (e.st === EState.Spawn) {
    if (e.stT >= e.stDur) { setSt(e, EState.Chase, 0); h.bossEvent(BossEv.Intro, 1, 0); }
    return;
  }

  // ---- phase changes
  const frac = e.hp / e.maxHp;
  if (b.phase === 0 && frac <= 0.66) {
    b.phase = 1;
    if (!b.ruffDone) { b.ruffDone = true; e.hyper = 1; b.crack = 0; h.bossEvent(BossEv.Ruff, 0, 0); }
  }
  if (b.phase === 1 && frac <= 0.33) { b.phase = 2; b.cd[BAtk.Howl] = secs(4); b.cadence *= 0.9; h.bossEvent(BossEv.Phase3, 0, 0); }

  // ---- adds: a few screeners keep the posse moving, never a horde
  if (--b.addsT <= 0) {
    b.addsT = secs(b.phase === 0 ? 24 : 18);
    let alive = 0;
    for (const o of h.enemies) if (!o.dead && o.type !== EType.Cerberus) alive++;
    if (alive < 5) h.spawnAdds(b.phase === 0 ? 3 : 4);
  }

  // ---- target
  if (--e.retargetT <= 0 || e.target < 0) {
    e.retargetT = secs(3);
    const ts = targets(h);
    let best = -1, bd = 1e9;
    for (const p of ts) { const d = len2(p.x - e.x, p.z - e.z) + h.rng.range(0, 8); if (d < bd) { bd = d; best = p.id; } }
    e.target = best;
  }
  const t = e.target >= 0 ? h.players.get(e.target) : undefined;
  if (t && (!isTargetable(t) || t.phase !== 0)) e.target = -1;
  const dx = t ? t.x - e.x : 0, dz = t ? t.z - e.z : 0;
  const dist = len2(dx, dz);

  let vx = 0, vz = 0;
  if (e.stunT > 0) {
    // staggered: knees buckle, everything is open
    if (e.st !== EState.Stun) { b.atk = BAtk.None; setSt(e, EState.Stun, e.stunT); }
    e.stunT--;
    if (e.stunT === 0) { setSt(e, EState.Recover, secs(0.6)); b.beat = secs(1.2); }
  } else if (e.st === EState.Chase) {
    // stalk: hold about KEEP metres, turn to face, pick the next attack on the beat
    if (t) {
      faceToward(e, t.x, t.z, 0.9);
      const want = dist - KEEP;
      if (Math.abs(want) > 2) { const s = clamp(want, -1, 1) * ENEMIES[EType.Cerberus].speed; vx = (dx / (dist || 1)) * s; vz = (dz / (dist || 1)) * s; }
      if (--b.beat <= 0) chooseAttack(e, b, h, t, dist);
    }
  } else if (e.st === EState.Windup) {
    tell(e, b, h, t);
    if (e.stT >= e.stDur) { b.hitMask = 0; setSt(e, EState.Attack, secs(EXEC[b.atk])); execStart(e, b, h); }
  } else if (e.st === EState.Attack) {
    const v = execute(e, b, h);
    vx = v.x; vz = v.z;
    if (e.stT >= e.stDur) { endAttack(e, b, secs(REC[b.atk])); }
  } else if (e.st === EState.Recover) {
    if (e.stT >= e.stDur) { b.atk = BAtk.None; setSt(e, EState.Chase, 0); b.beat = secs(h.rng.range(1.0, 1.8) * b.cadence); }
  } else setSt(e, EState.Chase, 0);

  // ---- move: it's heavy, it never leaves the arena, it walks over the terrain
  e.vx += (vx - e.vx) * 0.12; e.vz += (vz - e.vz) * 0.12;
  let nx = e.x + e.vx * DT, nz = e.z + e.vz * DT;
  const ar = h.world.arena;
  const ad = len2(nx - ar.x, nz - ar.z);
  if (ad > ARENA_R) { nx = ar.x + ((nx - ar.x) / ad) * ARENA_R; nz = ar.z + ((nz - ar.z) / ad) * ARENA_R; }
  const g = h.world.terrain(nx, nz);
  if (g !== NO_GROUND) { e.x = nx; e.z = nz; e.y = g; }
}

function faceToward(e: Enemy, x: number, z: number, rate: number) {
  const want = Math.atan2(-(x - e.x), -(z - e.z));
  const d = wrapAngle(want - e.yaw);
  e.yaw = wrapAngle(e.yaw + clamp(d, -rate * DT, rate * DT));
}

function chooseAttack(e: Enemy, b: BossState, h: BossHost, t: PlayerState, dist: number) {
  const alive = (hd: number) => b.mouth[hd] !== -1;
  const opts: { a: BAtk; w: number }[] = [];
  const add = (a: BAtk, w: number) => { if (b.cd[a] === 0) opts.push({ a, w }); };
  add(BAtk.Bark, dist < 18 ? 3 : 1);
  if (alive(Head.Magnet)) add(BAtk.Lunge, dist > 8 && dist < 30 ? 3 : 1);
  if (alive(Head.Furnace)) add(BAtk.Scorch, dist < 16 ? 4 : 0.5);
  if (alive(Head.Dynamo)) add(BAtk.Volley, dist > 10 ? 3 : 1.5);
  add(BAtk.Fling, dist > 14 ? 2.5 : 1);
  if (b.phase >= 2) add(BAtk.Howl, 6);
  if (!opts.length) { b.beat = secs(0.5); return; }
  let sum = 0;
  for (const o of opts) sum += o.w;
  let r = h.rng.range(0, sum);
  let pick = opts[opts.length - 1].a;
  for (const o of opts) { r -= o.w; if (r <= 0) { pick = o.a; break; } }
  b.atk = pick;
  e.mode = pick;
  const [lo, hi] = CD[pick];
  b.cd[pick] = secs(h.rng.range(lo, hi) * b.cadence);
  e.aimX = t.x; e.aimY = t.y; e.aimZ = t.z;
  setSt(e, EState.Windup, secs(TELL[pick] * (pick === BAtk.Howl ? 1 : Math.max(0.75, b.cadence))));
  h.bossEvent(BossEv.Attack, pick, attackHead(pick) + 1);
}

/** During the tell the aim tracks the target, then locks a beat before the attack. */
function tell(e: Enemy, b: BossState, h: BossHost, t: PlayerState | undefined) {
  const lock = secs(0.25);
  if (t && e.stT < e.stDur - lock) {
    if (b.atk !== BAtk.Howl) faceToward(e, t.x, t.z, b.atk === BAtk.Lunge ? 1.6 : 1.1);
    e.aimX = t.x; e.aimY = t.y; e.aimZ = t.z;
  }
  if (b.atk === BAtk.Lunge) {
    // the lane: from the boss through the aim point, overshooting a little
    const lx = e.aimX - e.x, lz = e.aimZ - e.z, l = len2(lx, lz) || 1;
    const run = Math.min(26, l + 5);
    b.lungeX = e.x + (lx / l) * run; b.lungeZ = e.z + (lz / l) * run;
  }
  void h;
}

function execStart(e: Enemy, b: BossState, h: BossHost) {
  switch (b.atk) {
    case BAtk.Scorch: b.sweep = e.yaw + 1.05; break;
    case BAtk.Fling: {
      // one boulder at everyone in range, two more around the target
      const sx = e.x, sy = e.y + 14 * S, sz = e.z;
      const g = BULLETS[BKind.Boulder].gravity;
      let n = 0;
      for (const p of targets(h)) {
        if (len2(p.x - e.x, p.z - e.z) > 55 || n >= 4) continue;
        const v = lobVelocity(sx, sy, sz, p.x, p.y, p.z, 1.45, g);
        h.fireBullet(BKind.Boulder, sx, sy, sz, v.vx, v.vy, v.vz, e.id); n++;
      }
      for (let i = 0; i < 2; i++) {
        const a = h.rng.range(0, Math.PI * 2), r = h.rng.range(4, 8);
        const tx = e.aimX + Math.cos(a) * r, tz = e.aimZ + Math.sin(a) * r;
        const v = lobVelocity(sx, sy, sz, tx, groundAt(h.world, tx, tz, e.aimY), tz, 1.6, g);
        h.fireBullet(BKind.Boulder, sx, sy, sz, v.vx, v.vy, v.vz, e.id);
      }
      break;
    }
    case BAtk.Howl: {
      // everyone the maw can see takes it; a pillar, a rock or a building is cover
      bossToWorld(e.x, e.y, e.z, e.yaw, HEADS[1].mx, HEADS[1].my, HEADS[1].mz, tmp);
      let mask = 0;
      for (const p of targets(h)) {
        const d = len2(p.x - e.x, p.z - e.z);
        if (d > 70) continue;
        if (!h.world.lineOfSight(tmp.x, tmp.y, tmp.z, p.x, p.y + 1.1, p.z)) continue;
        const kx = (p.x - e.x) / (d || 1), kz = (p.z - e.z) / (d || 1);
        if (h.hurtPlayer(p, 55, e.x, e.z, 0, kx * 18, kz * 18)) mask |= 1 << (p.id & 31);
      }
      b.howlLos = mask;
      h.bossEvent(BossEv.Howl, 0, 0);
      break;
    }
  }
}

const v2 = { x: 0, z: 0 };
function execute(e: Enemy, b: BossState, h: BossHost) {
  v2.x = 0; v2.z = 0;
  const k = e.stT / Math.max(1, e.stDur);
  switch (b.atk) {
    case BAtk.Bark: {
      // a ground shockwave rolling out from the treads: jump it
      const r = 2 + 24 * k;
      for (const p of targets(h)) {
        if (b.hitMask & (1 << (p.id & 31))) continue;
        const d = len2(p.x - e.x, p.z - e.z);
        const above = p.y - groundAt(h.world, p.x, p.z, p.y);
        if (Math.abs(d - r) < 1.3 && above < 1.1) {
          b.hitMask |= 1 << (p.id & 31);
          h.hurtPlayer(p, 20, e.x, e.z, 0, ((p.x - e.x) / (d || 1)) * 12, ((p.z - e.z) / (d || 1)) * 12);
        }
      }
      break;
    }
    case BAtk.Lunge: {
      const lx = b.lungeX - e.x, lz = b.lungeZ - e.z, l = len2(lx, lz);
      if (l > 0.6) { v2.x = (lx / l) * 22; v2.z = (lz / l) * 22; e.vx = v2.x; e.vz = v2.z; }
      const fx = lx / (l || 1), fz = lz / (l || 1);
      for (const p of targets(h)) {
        if (b.hitMask & (1 << (p.id & 31))) continue;
        if (len2(p.x - e.x, p.z - e.z) < 4.6 && p.y < e.y + 6) {
          b.hitMask |= 1 << (p.id & 31);
          // thrown sideways out of the lane
          const sx = (p.x - e.x) * fz - (p.z - e.z) * fx >= 0 ? 1 : -1;
          h.hurtPlayer(p, 45, e.x, e.z, 4, (fz * sx + fx * 0.5) * 20, (-fx * sx + fz * 0.5) * 20);
        }
      }
      break;
    }
    case BAtk.Scorch: {
      // the flame arc sweeps 120° across the front at ankle-to-chest height
      b.sweep = e.yaw + 1.05 - 2.1 * k;
      const fx = -Math.sin(b.sweep), fz = -Math.cos(b.sweep);
      for (const p of targets(h)) {
        if (b.hitMask & (1 << (p.id & 31))) continue;
        const px = p.x - e.x, pz = p.z - e.z, d = len2(px, pz);
        if (d > 18 || d < 2) continue;
        const along = (px * fx + pz * fz) / d;
        const above = p.y - groundAt(h.world, p.x, p.z, p.y);
        if (along > 0.96 && above < 2.2) {
          b.hitMask |= 1 << (p.id & 31);
          h.hurtPlayer(p, 25, e.x, e.z, 2, fx * 6, fz * 6);
        }
      }
      break;
    }
    case BAtk.Volley: {
      // three fans of slow bolts from the dynamo's mouth
      if (e.stT === 1 || e.stT === 25 || e.stT === 49) {
        bossToWorld(e.x, e.y, e.z, e.yaw, HEADS[1].mx, HEADS[1].my, HEADS[1].mz, tmp);
        const tp = e.target >= 0 ? h.players.get(e.target) : undefined;
        if (tp) { e.aimX = tp.x; e.aimY = tp.y + 1; e.aimZ = tp.z; }
        const ax = e.aimX - tmp.x, ay = e.aimY - tmp.y, az = e.aimZ - tmp.z;
        const l = Math.sqrt(ax * ax + ay * ay + az * az) || 1;
        const sp = BULLETS[BKind.Pellet].speed * 1.25;
        const base = Math.atan2(ax, az), flat = len2(ax, az) / l;
        const off = e.stT === 25 ? 0.5 : 0;
        for (let i = -3; i <= 3; i++) {
          const a = base + (i + off) * 0.13;
          h.fireBullet(BKind.Pellet, tmp.x, tmp.y, tmp.z, Math.sin(a) * sp * flat, (ay / l) * sp, Math.cos(a) * sp * flat, e.id);
        }
      }
      break;
    }
  }
  return v2;
}
